import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import ReturnProbe from "./pages/ReturnProbe";
import "./shell.css";
import { mark } from "./lib/stepLog";
import { logDevice, watchDevice } from "./lib/deviceInfo";

// t=0 of this page's step log.
mark("app_open", { path: window.location.pathname, link: new URLSearchParams(window.location.search).has("t") });
// The admin's Quick Scan tap, from the link's #hash (see the admin's
// lib/quickscan-early.js), so one line shows tap to this page on the phone:
// down = finger down (wall clock), click/paint/leave = ms after it on the admin,
// gap = the admin leaving until this page's navigation began, then this page's
// own server reply, parse and start (ms after its navigation began).
try {
  const h = new URLSearchParams(window.location.hash.slice(1));
  const down = Number(h.get("qt"));
  if (down > 0) {
    const nav = performance.getEntriesByType?.("navigation")?.[0] || {};
    const leave = Number(h.get("qn")) || 0;
    mark("admin_handoff", {
      click: Number(h.get("qc")) || 0,
      paint: Number(h.get("qp")) || 0,
      leave,
      gap: performance.timeOrigin - (down + leave),
      ttfb: nav.responseStart || 0,
      html: nav.responseEnd || 0,
      started: performance.now(),
      tapToHere: performance.timeOrigin + performance.now() - down,
      cached: nav.transferSize === 0,
    });
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  }
} catch {
  /* timing only */
}
// Which browser, phone and network this run is on, then what changes.
void logDevice();
watchDevice();

// The entry point. No SSR, no hydration, no framework.
//
// This app is static files on a host that runs nothing of ours, which is the
// security property the whole capture design rests on — there is no process
// here to hold a credential that could name a tenant. That also means there is
// no server-rendered HTML to hydrate, and therefore none of the hydration
// hazards the admin app has to live with (React escaping <style> children on
// the server but not the client, most memorably).
//
// StrictMode is ON deliberately, even though it double-mounts everything. This
// app opens camera streams and spends single-use tokens — both of which are
// exactly the kind of effect that breaks under a double mount, and both of
// which have already broken that way once. Finding that in development is the
// point.

createRoot(document.getElementById("root")).render(
  <StrictMode>
    {new URLSearchParams(window.location.search).get("debug") === "return" ? <ReturnProbe /> : <App />}
  </StrictMode>,
);
